import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoChevronBack, IoChevronForward, IoPlaySkipBack, IoPlaySkipForward } from 'react-icons/io5';
import PlaybackViewerCard from './PlaybackViewerCard';
import type { PlaybackViewerDownloadState } from './PlaybackViewerCard';
import type { PlaybackLayer } from './types';
import type { LayerFeature, PanoramaState, SyncMode } from 'components/ImageViewer/types';
import type { StaticViewportState } from 'components/ImageViewer/StaticImageViewer';
import './style.scss';

export type PlaybackDownloadAllControls = {
  readonly canDownload: boolean;
  readonly downloadAll: () => void;
};

interface Props {
  layers: PlaybackLayer[];
  isLoading?: boolean;
  currentIndex: number;
  syncMode: SyncMode;
  focusOwnerLayerKey: string | null;
  focusMessage?: string | null;
  currentSyncValue: number | null;
  maxIndex: number;
  onNavigate: (direction: 'next' | 'previous' | 'first' | 'last') => void;
  onSetSyncModeFromFeature: (
    layerKey: string,
    feature: LayerFeature,
    mode: SyncMode,
  ) => void;
  onPanoramaStateChange: (layerKey: string, state: PanoramaState | null) => void;
  onDownloadAllControlsChange?: (controls: PlaybackDownloadAllControls | null) => void;
}

const PlaybackGrid: React.FC<Props> = ({
  layers,
  isLoading = false,
  currentIndex,
  syncMode,
  focusOwnerLayerKey,
  focusMessage = null,
  currentSyncValue,
  maxIndex,
  onNavigate,
  onSetSyncModeFromFeature,
  onPanoramaStateChange,
  onDownloadAllControlsChange,
}) => {
  const [downloadStateByLayer, setDownloadStateByLayer] = useState<
    Record<string, PlaybackViewerDownloadState>
  >({});
  const [staticViewportByLayer, setStaticViewportByLayer] = useState<
    Record<string, StaticViewportState | null>
  >({});

  const handleDownloadStateChange = useCallback((state: PlaybackViewerDownloadState) => {
    setDownloadStateByLayer((prev) => ({ ...prev, [state.layerKey]: state }));
  }, []);

  const handleStaticViewportChange = useCallback(
    (layerKey: string, state: StaticViewportState) => {
      setStaticViewportByLayer((prev) => ({ ...prev, [layerKey]: state }));
    },
    [],
  );

  const downloadableStates = useMemo(
    () => Object.values(downloadStateByLayer).filter((state) => state.canDownload && !!state.downloadSrc),
    [downloadStateByLayer],
  );

  const handleDownloadAll = useCallback(() => {
    // Browsers may block or prompt for each file when many downloads fire from one click.
    for (const state of downloadableStates) {
      if (!state.downloadSrc) continue;
      const anchor = document.createElement('a');
      anchor.href = state.downloadSrc;
      anchor.download = state.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  }, [downloadableStates]);

  useEffect(() => {
    if (!onDownloadAllControlsChange) {
      return;
    }
    if (isLoading || layers.length <= 1) {
      onDownloadAllControlsChange(null);
      return;
    }
    onDownloadAllControlsChange({
      canDownload: downloadableStates.length > 0,
      downloadAll: handleDownloadAll,
    });
  }, [
    downloadableStates.length,
    handleDownloadAll,
    isLoading,
    layers.length,
    onDownloadAllControlsChange,
  ]);

  useEffect(() => {
    return () => {
      onDownloadAllControlsChange?.(null);
    };
  }, [onDownloadAllControlsChange]);

  const count = layers.length;
  const gridModifier =
    count === 2
      ? ' playback-grid--row'
      : count === 1 || count === 3 || count === 4
        ? ` playback-grid--${count}`
        : count <= 6
          ? ' playback-grid--6'
          : count <= 9
            ? ' playback-grid--9'
            : ' playback-grid--many';

  return (
    <div className="playback-grid-container playback-grid-container--embedded">
      <div className={`playback-grid${gridModifier}`}>
        {isLoading && (
          <div className="playback-grid-loading">
            <div className="xa-loader-content">
              <svg
                aria-hidden="true"
                className="xa-loader"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          </div>
        )}
        {!isLoading && layers.map((layer) => (
          <PlaybackViewerCard
            key={layer.layerKey}
            layer={layer}
            syncMode={syncMode}
            targetSyncValue={currentSyncValue}
            showFocusButton={layers.length > 1}
            isFocusOwner={focusOwnerLayerKey === layer.layerKey}
            onSetSyncModeFromFeature={onSetSyncModeFromFeature}
            onPanoramaStateChange={onPanoramaStateChange}
            onDownloadStateChange={handleDownloadStateChange}
            staticViewportState={staticViewportByLayer[layer.layerKey] ?? null}
            onStaticViewportStateChange={(state) => handleStaticViewportChange(layer.layerKey, state)}
          />
        ))}
      </div>
      {!isLoading && (
        <div className="modal-footer image-viewer-footer playback-nav-bar">
          {focusMessage && (
            <div className="image-viewer-sync-limit-msg" role="status">
              {focusMessage}
            </div>
          )}
          <div className="image-viewer-nav-buttons">
            <button
              className="xa-button primary image-viewer-nav-btn playback-nav-btn"
              onClick={() => onNavigate('first')}
              disabled={currentIndex <= 0}
              title="First image"
            >
              <IoPlaySkipBack />
              <span>First</span>
            </button>
            <button
              className="xa-button primary image-viewer-nav-btn playback-nav-btn"
              onClick={() => onNavigate('previous')}
              disabled={currentIndex <= 0}
              title="Previous image"
            >
              <IoChevronBack />
              <span>Previous</span>
            </button>
            <span className="image-viewer-nav-index playback-nav-index">
              {currentIndex + 1}&nbsp;/&nbsp;{maxIndex + 1}
            </span>
            <button
              className="xa-button primary image-viewer-nav-btn playback-nav-btn"
              onClick={() => onNavigate('next')}
              disabled={currentIndex >= maxIndex}
              title="Next image"
            >
              <span>Next</span>
              <IoChevronForward />
            </button>
            <button
              className="xa-button primary image-viewer-nav-btn playback-nav-btn"
              onClick={() => onNavigate('last')}
              disabled={currentIndex >= maxIndex}
              title="Last image"
            >
              <span>Last</span>
              <IoPlaySkipForward />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaybackGrid;
